import { calculateGrossMarginFromUnitPrice } from '@calculations/price/utilities/gross-margin';
import { calculateTotal } from '@calculations/price/utilities/total';
import { SnapRoundingOptionsCustom } from '@calculations/round';
import { PriceStrategy } from '@snap-types/models/enums';
import type { PriceStrategyConfigWithRelations } from '@snap-types/models/price-strategy-config';
import { SnapSuggestionStatus } from '@snap-types/models/sort-status';
import { Decimal } from '@snap-types/types/decimal';
import { WritableQuoteBuilderStateType } from '@ui/features/quote-builder/types/state';

import {
  getQuoteLineOrThrow,
  getQuotePriceOrThrow,
} from '../../utilities/action-guards';
import { buildElementId } from '../../utilities/element-id';
import { buildIssueId } from '../../utilities/issue-id';
import { deleteIssue, upsertIssue } from '../../utilities/issue-managers';
import { applyQuotePriceRecalcResult } from '../utilities/apply-quote-price-recalc-result';
import { handleQuantityChangeFallback } from '../utilities/handle-quantity-change-fallback';
import { recalculateQuantityAwareLeadTime } from './on-part-lead-time-change';

export function onPartQuantityChange(
  state: WritableQuoteBuilderStateType,
  params: {
    quoteLineUuid: string;
    quotePriceUuid: string;
    quantity: Decimal;
    priceStrategyConfigs: PriceStrategyConfigWithRelations[];
  },
) {
  const quoteLine = getQuoteLineOrThrow(state, params.quoteLineUuid);
  const quotePrice = getQuotePriceOrThrow(
    state,
    params.quoteLineUuid,
    params.quotePriceUuid,
  );

  const usingCalculatedGrossMargin =
    quotePrice.automatedGrossMargin &&
    quotePrice.grossMargin?.eq(quotePrice.automatedGrossMargin);

  const usingCalculatedBaseGrossMargin =
    quotePrice.baseGrossMargin &&
    quotePrice.automatedBaseGrossMargin &&
    quotePrice.baseGrossMargin?.eq(quotePrice.automatedBaseGrossMargin);

  const usingCalculatedContributionMargin =
    quotePrice.contributionMargin &&
    quotePrice.automatedContributionMargin &&
    quotePrice.contributionMargin?.eq(quotePrice.automatedContributionMargin);

  const { priceStrategy } = quotePrice;

  const inputs = {
    state,
    quoteLine,
    quotePrice,
    quantity: params.quantity,
    priceStrategyConfigs: params.priceStrategyConfigs,
  };

  const result = handleQuantityChangeFallback({
    currentStrategy: priceStrategy,
    priceStrategyConfigs: params.priceStrategyConfigs,
    inputs,
  });

  const calculationIssueId = buildIssueId({
    area: 'quotePrice',
    quoteLineUuid: params.quoteLineUuid,
    quotePriceUuid: params.quotePriceUuid,
    type: 'invalid',
    field: 'quantity',
  });

  // Update quotePrice.priceStrategy if fallback succeeded
  if (
    (result.status === 'success' || result.status === 'no-op') &&
    result.strategy &&
    result.strategy !== priceStrategy
  ) {
    quotePrice.priceStrategy = result.strategy;
  }

  if (result.status === 'failure' || result.status === 'fallback-failure') {
    // @TODO(NT): Our store should be integrated with toast notifications
    console.error('Failed to calculate price', result.reason);

    upsertIssue(state, calculationIssueId, {
      title: 'Could not calculate price',
      description: result.reason,
      severity: 'warning',
      type: 'invalid',
      area: 'quotePrice',
      quoteLineUuid: params.quoteLineUuid,
      quotePriceUuid: params.quotePriceUuid,
      action: {
        type: 'modify',
        elementId: buildElementId({
          uuid: params.quotePriceUuid,
          field: 'quantity',
        }),
      },
    });
    return;
  }

  quotePrice.quantity = params.quantity;
  quotePrice.userQuantity = params.quantity;

  // Quantity-aware lead times depend on the quantity, so re-derive this price's
  // lead time (a no-op for quantity-independent strategies).
  recalculateQuantityAwareLeadTime(state, {
    quoteLineUuid: params.quoteLineUuid,
    quotePriceUuid: params.quotePriceUuid,
    quantity: params.quantity,
  });

  if (result.status === 'success') {
    deleteIssue(state, calculationIssueId);

    const unitPrice = result.unitPrice;

    quotePrice.calculatedUnitPrice = unitPrice;
    quotePrice.unitPrice = unitPrice;
    quotePrice.userUnitPrice = null;

    quotePrice.unitCost = result.unitCost ?? null;

    quotePrice.grossMargin = result.grossMargin ?? null;

    quotePrice.unitBurdenCost = result.unitBurdenCost ?? null;
    quotePrice.unitLaborCost = result.unitLaborCost ?? null;
    quotePrice.unitMaterialCost = result.unitMaterialCost ?? null;
    quotePrice.unitServiceCost = result.unitServiceCost ?? null;
    quotePrice.unitVariableCosts = result.unitVariableCosts ?? null;
    quotePrice.contributionMargin = result.contributionMargin ?? null;

    quotePrice.baseUnitPrice = result.baseUnitPrice ?? null;
    quotePrice.priceAdjustmentAmount = result.priceAdjustmentAmount ?? null;

    quotePrice.unitPriceAddition = result.unitPriceAddition ?? null;
    quotePrice.changesUnitPrice = result.changesUnitPrice ?? false;

    quotePrice.totalPrice = calculateTotal(
      unitPrice,
      params.quantity,
      SnapRoundingOptionsCustom(state),
    );

    quotePrice.requiresUnsupportedCalculations =
      result.requiresUnsupportedCalculations ?? [];

    applyQuotePriceRecalcResult(state, {
      quoteLineUuid: params.quoteLineUuid,
      quotePriceUuid: params.quotePriceUuid,
      quotePrice,
      patch: {
        quantity: params.quantity,
        unitPrice,
        unitCost: result.unitCost ?? null,
        grossMargin: result.grossMargin ?? null,
        contributionMargin: result.contributionMargin ?? null,
        baseGrossMargin: quotePrice.baseGrossMargin,
      },
      rawFieldsToClear: [
        'unitPrice',
        'unitCost',
        'unitVariableCosts',
        'grossMargin',
        'contributionMargin',
        'priceAdjustmentAmount',
      ],
    });

    // Use the updated strategy (may have changed due to fallback)
    const finalStrategy = quotePrice.priceStrategy;

    switch (finalStrategy) {
      case PriceStrategy.PriceSegmentation:
      case PriceStrategy.PriceSegmentationWithTargetGrossMargin:
      case PriceStrategy.CAPPriceSegmentation:
        // When changing quantity, the existing base gross margin remains unchanged;
        // If that value was already the calculated value, now ALL values are calculated, and the user has
        // accepted the price
        if (usingCalculatedBaseGrossMargin) {
          quotePrice.suggestedPriceStatus = SnapSuggestionStatus.ACCEPTED;
        }
        break;

      case PriceStrategy.TargetContributionMargin:
        // When changing quantity, the existing contribution margin remains unchanged;
        // If that value was already the calculated value, now ALL values are calculated, and the user has
        // accepted the price
        if (usingCalculatedContributionMargin) {
          quotePrice.suggestedPriceStatus = SnapSuggestionStatus.ACCEPTED;
        }
        break;

      case PriceStrategy.TargetGrossMargin:
      case PriceStrategy.RAFTargetGrossMargin:
        // When changing quantity, the existing gross margin remains unchanged;
        // If that value was already the calculated value, now ALL values are calculated, and the user has
        // accepted the price
        if (usingCalculatedGrossMargin) {
          quotePrice.suggestedPriceStatus = SnapSuggestionStatus.ACCEPTED;
        }
        break;
      default:
        quotePrice.suggestedPriceStatus = SnapSuggestionStatus.ACCEPTED;
    }
  }

  if (result.status === 'no-op') {
    quotePrice.totalPrice = calculateTotal(
      quotePrice.unitPrice,
      params.quantity,
      SnapRoundingOptionsCustom(state),
    );
    quotePrice.grossMargin = result.grossMargin ?? null;
    quotePrice.contributionMargin = result.contributionMargin ?? null;

    applyQuotePriceRecalcResult(state, {
      quoteLineUuid: params.quoteLineUuid,
      quotePriceUuid: params.quotePriceUuid,
      quotePrice,
      patch: {
        quantity: params.quantity,
        unitPrice: quotePrice.unitPrice,
        grossMargin: result.grossMargin ?? null,
        contributionMargin: result.contributionMargin ?? null,
        baseGrossMargin: quotePrice.baseGrossMargin,
      },
      rawFieldsToClear: ['grossMargin', 'contributionMargin'],
    });
  }

  // Handles strategies that need to calculate a unit cost and margin, but not a unit price
  if (result.status === 'skip-price') {
    quotePrice.unitCost = result.unitCost ?? null;

    const { unitPrice } = quotePrice;

    quotePrice.totalPrice = calculateTotal(
      unitPrice,
      params.quantity,
      SnapRoundingOptionsCustom(state),
    );

    if (result.unitCost) {
      const grossMargin = calculateGrossMarginFromUnitPrice({
        unitCost: result.unitCost,
        unitPrice,
      });

      quotePrice.grossMargin = grossMargin;
      quotePrice.userGrossMargin = null;
    }

    applyQuotePriceRecalcResult(state, {
      quoteLineUuid: params.quoteLineUuid,
      quotePriceUuid: params.quotePriceUuid,
      quotePrice,
      patch: {
        quantity: params.quantity,
        unitPrice,
        unitCost: result.unitCost ?? null,
        grossMargin: quotePrice.grossMargin,
        contributionMargin: quotePrice.contributionMargin,
        baseGrossMargin: quotePrice.baseGrossMargin,
      },
      rawFieldsToClear: ['unitCost', 'grossMargin'],
    });
  }

  const allPricesAccepted = Object.values(quoteLine.quotePrices).every(
    (quotePrice) =>
      quotePrice.suggestedPriceStatus === SnapSuggestionStatus.ACCEPTED,
  );
  quoteLine.suggestedPricesStatus = allPricesAccepted
    ? SnapSuggestionStatus.ACCEPTED
    : SnapSuggestionStatus.REJECTED;

  const allPricesAutomated = Object.values(quoteLine.quotePrices).every(
    (quotePrice) => quotePrice.automatedQuantity?.eq(quotePrice.quantity),
  );

  // A QuoteLine is automatable if ALL of the price lines started with automated values (
  // meaning they were added at the same time the quote line was added) AND are user-editable
  // values were accepted
  quoteLine.automatable =
    quoteLine.leadTimeSuggestionStatus === SnapSuggestionStatus.ACCEPTED &&
    allPricesAutomated &&
    quoteLine.suggestedPricesStatus === SnapSuggestionStatus.ACCEPTED;
}
